import { useSyncExternalStore } from 'react';
import { ApiError, getAccessToken } from './api';
import { errors, translateError } from '../i18n/errors';
import { currentLocale } from './locale';

export interface ToastAction {
  label: string;
  /** An in-app route. */
  to: string;
}

export interface Toast {
  id: number;
  kind: 'success' | 'error';
  message: string;
  action?: ToastAction;
}

const DURATION_MS = 3000;

/**
 * Module scope rather than React context: the query client raises toasts from
 * its caches, and it is created in `main.tsx`, outside any component.
 */
let toasts: readonly Toast[] = [];
let nextId = 0;
const timers = new Map<number, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function schedule(id: number): void {
  clearTimeout(timers.get(id));
  timers.set(
    id,
    setTimeout(() => dismissToast(id), DURATION_MS),
  );
}

function push(kind: Toast['kind'], message: string, action?: ToastAction) {
  // Several rows failing at once say the same thing; restart the toast already
  // on screen instead of stacking copies of it.
  const existing = toasts.find((t) => t.kind === kind && t.message === message);
  if (existing) {
    schedule(existing.id);
    return;
  }
  const id = nextId++;
  toasts = [...toasts, { id, kind, message, action }];
  schedule(id);
  emit();
}

export function dismissToast(id: number): void {
  clearTimeout(timers.get(id));
  timers.delete(id);
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export const toast = {
  success: (message: string, options?: { action?: ToastAction }) =>
    push('success', message, options?.action),
  error: (message: string) => push('error', message),
};

/**
 * What the query client calls for every failed query and mutation, and the one
 * place an error becomes text: the code's translation, else the screen's own
 * failure text, else (a network failure, a bug) a generic line. Once the
 * session is over the guards are already sending the user to the login page,
 * and every request still in flight failing on the way out is noise.
 */
export function toastError(error: Error): void {
  if (getAccessToken() === null) return;
  if (!(error instanceof ApiError)) {
    toast.error(errors[currentLocale()].network);
    return;
  }
  toast.error(error.code ? translateError(error.code) : error.message);
}

/**
 * Open modal dialogs, innermost last. A modal makes everything outside it
 * inert, top layer included, so the toaster renders inside the top one —
 * the only place its dismiss buttons and links still take a click.
 */
let hosts: readonly HTMLElement[] = [];

export function addToastHost(host: HTMLElement): () => void {
  hosts = [...hosts, host];
  emit();
  return () => {
    hosts = hosts.filter((h) => h !== host);
    emit();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useToasts(): readonly Toast[] {
  return useSyncExternalStore(subscribe, () => toasts);
}

export function useToastHost(): HTMLElement | null {
  return useSyncExternalStore(subscribe, () => hosts.at(-1) ?? null);
}
